import React, {useEffect, useMemo, useState} from 'react';
import {getCinemaZoneMovies} from '../services/api';
import {CinemaZoneMovie} from '../types/api';

const MOVIE_LIMIT = 8;
const CINEMA_ZONE_SITE_URL = process.env.REACT_APP_CINEMA_ZONE_SITE_URL || '';

const formatRentalPrice = (price?: string) => {
  const amount = Number.parseFloat(price || '');

  if (Number.isNaN(amount)) {
    return 'Rental available';
  }

  return `Rental $${amount.toFixed(2)}`;
};

const getMovieTitle = (movie: CinemaZoneMovie) => movie.title || movie.titleEn || 'Cinema Zone movie';

const getMovieUrl = (movieId: number) => {
  const baseUrl = CINEMA_ZONE_SITE_URL.replace(/\/$/, '');

  return `${baseUrl}/movies/${movieId}`;
};

const getGenreLabel = (movie: CinemaZoneMovie) => {
  if (movie.genres && movie.genres.length > 0) {
    return movie.genres.slice(0, 2).join(', ');
  }

  return movie.genre || movie.language || movie.languageEn || 'Movie rental';
};

export const CinemaZoneMovies: React.FC = () => {
  const [movies, setMovies] = useState<CinemaZoneMovie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMovies = async () => {
      try {
        const response = await getCinemaZoneMovies();
        setMovies(response.data || []);
      } catch (err) {
        setError('Cinema Zone movies are currently unavailable');
        console.error('Error loading Cinema Zone movies:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMovies();
  }, []);

  const availableMovies = useMemo(
    () => movies.filter((movie) => !movie.status || movie.status === 'active').slice(0, MOVIE_LIMIT),
    [movies]
  );

  if (loading) {
    return (
      <section className="rounded-lg bg-white p-6 text-center text-sm text-indigo-800 shadow-sm">
        Loading Cinema Zone rentals...
      </section>
    );
  }

  if (error || availableMovies.length === 0) {
    return null;
  }

  return (
    <section className="rounded-lg bg-white p-4 shadow-sm sm:p-6">
      <div className="mb-4">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">
            Cinema Zone
          </span>
          <span className="text-xs font-medium text-indigo-700">Separate rental catalogue</span>
        </div>
        <h3 className="text-xl font-semibold text-gray-900">Movies available to rent</h3>
        <p className="mt-2 text-sm leading-6 text-gray-700">
          These titles are offered by Cinema Zone as separate movie rentals. They are not included with,
          or unlocked by, the Viu Ad-Free Pass on this page.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {availableMovies.map((movie) => {
          const imageUrl = movie.thumbnailUrl || movie.imageUrl;

          return (
            <a
              key={movie.id}
              href={getMovieUrl(movie.id)}
              // target="_blank"
              rel="noopener noreferrer"
              className="group overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-indigo-100 transition-shadow hover:shadow-md"
            >
              <div className="relative bg-indigo-100" style={{paddingBottom: '56.8%'}}>
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={getMovieTitle(movie)}
                    className="absolute inset-0 h-full w-full object-cover transition-opacity group-hover:opacity-90"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center px-3 text-center text-sm font-medium text-indigo-700">
                    Cinema Zone
                  </div>
                )}
              </div>
              <div className="p-3">
                <h4 className="line-clamp-2 min-h-[2.5rem] text-sm font-semibold leading-5 text-gray-900 transition-colors group-hover:text-indigo-700">
                  {getMovieTitle(movie)}
                </h4>
                <p className="mt-2 truncate text-xs text-gray-500">{getGenreLabel(movie)}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-indigo-100 px-2 py-1 text-xs font-semibold text-indigo-700">
                    {formatRentalPrice(movie.price)}
                  </span>
                  {movie.durationMinutes && (
                    <span className="text-xs text-gray-500">{movie.durationMinutes} min</span>
                  )}
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
};
